import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import SaveStudioAssetButton from "../SaveStudioAssetButton";
import FieldHint from "../FieldHint";
import useI18n from "../../../i18n/useI18n";

import MotionSurface from "../../../motion/studio/MotionSurface";
import ToolResultSkeleton from "../../loaders/ToolResultSkeleton";

import ResultCard from "../../ui/ResultCard";
import ActionRow from "../../ui/ActionRow";
import CopyButton from "../../ui/CopyButton";

import AIActionButton from "../../ui/buttons/AIActionButton";
import UtilityButton from "../../ui/buttons/UtilityButton";

const STUDIO_USAGE_REFRESH_KEY = "hookora_studio_usage_refresh";
const THEME_KEY = "hookora_theme";

const getPreferredLanguage = () => {
  if (typeof window === "undefined") return "en";
  return localStorage.getItem("hookora_language") || "en";
};

const notifyStudioUsageUpdated = () => {
  const value = String(Date.now());
  localStorage.setItem(STUDIO_USAGE_REFRESH_KEY, value);
  window.dispatchEvent(new CustomEvent("hookora:studio-usage-updated"));
};

const formatArchetypeLabel = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return raw
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getScoreTone = (score) => {
  const safe = Number(score) || 0;
  if (safe >= 85) {
    return {
      bar: "#16a34a",
      pillBg: "rgba(22,163,74,0.14)",
      pillText: "#16a34a",
    };
  }
  if (safe >= 70) {
    return {
      bar: "#2563eb",
      pillBg: "rgba(37,99,235,0.14)",
      pillText: "#2563eb",
    };
  }
  return {
    bar: "#f59e0b",
    pillBg: "rgba(245,158,11,0.16)",
    pillText: "#b45309",
  };
};

const TitleTool = ({ sharedTopic, setSharedTopic }) => {
  const { t } = useI18n();

  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [audience, setAudience] = useState("");
  const [goal, setGoal] = useState("");
  const [angle, setAngle] = useState("");
  const [keywords, setKeywords] = useState("");
  const [brandVoice, setBrandVoice] = useState("");
  const [avoidPhrases, setAvoidPhrases] = useState("");
  const [extraContext, setExtraContext] = useState("");

  const [titles, setTitles] = useState([]);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const syncTheme = () => setTheme(localStorage.getItem(THEME_KEY) || "light");
    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);
    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      cardBgStrong: isDark ? "rgba(15,23,42,0.92)" : "#ffffff",
      border: isDark ? "rgba(148,163,184,0.22)" : "#e5e7eb",
      borderStrong: isDark ? "rgba(148,163,184,0.34)" : "#d1d5db",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputText: isDark ? "#f8fafc" : "#111827",
      trackBg: isDark ? "#1e293b" : "#e5e7eb",
      panelBg: isDark ? "rgba(30,41,59,0.55)" : "#f8fafc",
      buttonBg: isDark ? "#e2e8f0" : "#111827",
      buttonText: isDark ? "#020617" : "#ffffff",
      secondaryButtonBg: isDark ? "#1e293b" : "#e5e7eb",
      secondaryButtonText: isDark ? "#f8fafc" : "#111827",
      dangerBg: isDark ? "rgba(127,29,29,0.25)" : "#fef2f2",
      dangerText: isDark ? "#fecaca" : "#b91c1c",
      label: isDark ? "#e2e8f0" : "#111827",
      heroBg: isDark
        ? "linear-gradient(135deg, rgba(30,41,59,0.84), rgba(15,23,42,0.96))"
        : "linear-gradient(135deg, #f8fafc, #ffffff)",
      heroBorder: isDark ? "rgba(59,130,246,0.24)" : "#bfdbfe",
      heroBadgeBg: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
      heroBadgeText: isDark ? "#bfdbfe" : "#1d4ed8",
      heroBadgeBorder: isDark ? "rgba(96,165,250,0.24)" : "#bfdbfe",
      shadow: isDark
        ? "0 16px 34px rgba(2,6,23,0.34)"
        : "0 12px 28px rgba(15,23,42,0.06)",
      shadowSoft: isDark
        ? "0 10px 24px rgba(2,6,23,0.22)"
        : "0 6px 18px rgba(15,23,42,0.05)",
      chipBg: isDark ? "rgba(30,41,59,0.9)" : "#f1f5f9",
      chipText: isDark ? "#cbd5e1" : "#475569",
    }),
    [isDark]
  );

  const inputStyle = {
    width: "100%",
    padding: "13px 14px",
    borderRadius: "12px",
    border: `1px solid ${colors.borderStrong}`,
    outline: "none",
    boxSizing: "border-box",
    background: colors.inputBg,
    color: colors.inputText,
    fontSize: "14px",
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: "86px",
    resize: "vertical",
  };

  const labelStyle = {
    display: "flex",
    alignItems: "center",
    marginBottom: "6px",
    color: colors.label,
    fontSize: "14px",
    fontWeight: 600,
  };

  const clearResults = () => {
    setTitles([]);
    setCopiedId(null);
    setError("");
  };

  const resetForm = () => {
    setSharedTopic("");
    setAudience("");
    setGoal("");
    setAngle("");
    setKeywords("");
    setBrandVoice("");
    setAvoidPhrases("");
    setExtraContext("");
    setTitles([]);
    setCopiedId(null);
    setError("");
  };

  const generateTitles = async () => {
    if (!sharedTopic.trim()) {
      setError(t("titleTool.errors.topicRequired", "Please enter a topic first."));
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await API.post("/studio/titles", {
        topic: sharedTopic.trim(),
        audience,
        goal,
        angle,
        keywords,
        brand_voice: brandVoice,
        avoid_phrases: avoidPhrases,
        extra_context: extraContext,
        language: getPreferredLanguage(),
      });

      setTitles(Array.isArray(response?.data?.titles) ? response.data.titles : []);
      setCopiedId(null);
      notifyStudioUsageUpdated();
    } catch (err) {
      console.error("Failed to generate titles:", err);
      setError(err?.response?.data?.detail || t("titleTool.errors.generateFailed", "Failed to generate titles."));
    } finally {
      setLoading(false);
    }
  };

  const copyTitle = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy title:", err);
    }
  };

  const renderScoreBar = (score) => {
    const safe = Number(score) || 0;
    const tone = getScoreTone(safe);

    return (
      <div
        style={{
          marginTop: "10px",
          height: "10px",
          borderRadius: "999px",
          background: colors.trackBg,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${Math.max(0, Math.min(100, safe))}%`,
            height: "100%",
            borderRadius: "999px",
            background: tone.bar,
          }}
        />
      </div>
    );
  };

  return (
    <MotionSurface>
    <div style={{ display: "grid", gap: "18px" }}>
      <div
        style={{
          border: `1px solid ${colors.heroBorder}`,
          borderRadius: "20px",
          padding: "18px",
          background: colors.heroBg,
          boxShadow: colors.shadow,
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "6px 10px",
            borderRadius: "999px",
            background: colors.heroBadgeBg,
            color: colors.heroBadgeText,
            border: `1px solid ${colors.heroBadgeBorder}`,
            fontSize: "12px",
            fontWeight: 800,
            letterSpacing: "0.03em",
            marginBottom: "12px",
          }}
        >
          {t("titleTool.hero.badge", "Click-focused tool")}
        </div>

        <div style={{ color: colors.text, fontSize: "24px", fontWeight: 800, marginBottom: "8px" }}>
          {t("titleTool.hero.title", "Titles")}
        </div>

        <div style={{ color: colors.textSoft, lineHeight: "1.7", maxWidth: "880px" }}>
          {t(
            "titleTool.hero.description",
            "Use this tool to generate clickable title options for videos and content pieces. Add search intent, angle, and context so Hookora can return stronger title variations that feel sharper, clearer, and more likely to earn attention."
          )}
        </div>
      </div>

      <div
        style={{
          border: `1px solid ${colors.border}`,
          borderRadius: "18px",
          padding: "18px",
          background: colors.cardBg,
          boxShadow: colors.shadowSoft,
        }}
      >
        <div style={{ display: "grid", gap: "14px" }}>
          <div>
            <label style={labelStyle}>
              {t("titleTool.fields.topic.label", "Topic")}
              <FieldHint text={t("titleTool.fields.topic.hint", "The main subject of the video or content piece. The clearer your topic, the better the title variations.")} />
            </label>
            <textarea
              value={sharedTopic}
              onChange={(e) => setSharedTopic(e.target.value)}
              placeholder={t("titleTool.fields.topic.placeholder", "What video or topic are these titles for?")}
              style={textareaStyle}
            />
          </div>

          <div
            style={{
              padding: "14px",
              borderRadius: "14px",
              background: colors.panelBg,
              border: `1px solid ${colors.border}`,
            }}
          >
            <div style={{ fontWeight: 700, color: colors.text, marginBottom: "12px" }}>
              {t("titleTool.contextSectionTitle", "Title Context")}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "12px",
              }}
            >
              <div>
                <label style={labelStyle}>
                  {t("titleTool.fields.audience.label", "Audience")}
                  <FieldHint text={t("titleTool.fields.audience.hint", "Who the content is for. Example: students, creators, ecommerce founders, Nigerian freelancers.")} />
                </label>
                <input
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder={t("titleTool.fields.audience.placeholder", "Audience")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("titleTool.fields.goal.label", "Goal")}
                  <FieldHint text={t("titleTool.fields.goal.hint", "What the title should optimize for. Example: clicks, curiosity, search traffic, authority, conversions.")} />
                </label>
                <input
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  placeholder={t("titleTool.fields.goal.placeholder", "Goal")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("titleTool.fields.angle.label", "Angle")}
                  <FieldHint text={t("titleTool.fields.angle.hint", "The framing of the title. Example: mistake-led, beginner-friendly, contrarian, results-focused.")} />
                </label>
                <input
                  value={angle}
                  onChange={(e) => setAngle(e.target.value)}
                  placeholder={t("titleTool.fields.angle.placeholder", "Angle")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("titleTool.fields.keywords.label", "Keywords")}
                  <FieldHint text={t("titleTool.fields.keywords.hint", "Important search terms to weave into the titles naturally. Separate them with commas.")} />
                </label>
                <input
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder={t("titleTool.fields.keywords.placeholder", "Keywords (comma separated)")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("titleTool.fields.brandVoice.label", "Brand Voice")}
                  <FieldHint text={t("titleTool.fields.brandVoice.hint", "How the title should feel. Example: sharp, premium, direct, playful, bold.")} />
                </label>
                <input
                  value={brandVoice}
                  onChange={(e) => setBrandVoice(e.target.value)}
                  placeholder={t("titleTool.fields.brandVoice.placeholder", "Brand voice")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("titleTool.fields.avoidPhrases.label", "Avoid Phrases")}
                  <FieldHint text={t("titleTool.fields.avoidPhrases.hint", "Words or repeated patterns you do not want the title generator to use.")} />
                </label>
                <input
                  value={avoidPhrases}
                  onChange={(e) => setAvoidPhrases(e.target.value)}
                  placeholder={t("titleTool.fields.avoidPhrases.placeholder", "Avoid phrases")}
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginTop: "12px" }}>
              <label style={labelStyle}>
                {t("titleTool.fields.extraContext.label", "Extra Context")}
                <FieldHint text={t("titleTool.fields.extraContext.hint", "Add proof points, specific results, target emotion, or extra positioning notes to improve title quality.")} />
              </label>
              <textarea
                value={extraContext}
                onChange={(e) => setExtraContext(e.target.value)}
                placeholder={t("titleTool.fields.extraContext.placeholder", "Extra context, unique proof, result, example, or positioning note")}
                style={textareaStyle}
              />
            </div>
          </div>

          {error && (
            <div
              style={{
                padding: "12px 14px",
                borderRadius: "12px",
                background: colors.dangerBg,
                color: colors.dangerText,
              }}
            >
              {error}
            </div>
          )}

          <ActionRow>
            <AIActionButton isDark={isDark}
              onClick={generateTitles}
              disabled={loading}
            >
              {loading
                ? t("titleTool.actions.generating", "Generating...")
                : t("titleTool.actions.generate", "Generate Titles")}
            </AIActionButton>

            <UtilityButton isDark={isDark}
              type="button"
              onClick={clearResults}
            >
              {t("titleTool.actions.clearResults", "Clear Results")}
            </UtilityButton>

            <UtilityButton isDark={isDark}
              type="button"
              onClick={resetForm}
            >
              {t("titleTool.actions.resetForm", "Reset Form")}
            </UtilityButton>
          </ActionRow>
        </div>
      </div>

      {!!titles.length && (
        <div
          style={{
            color: colors.textMuted,
            fontSize: "14px",
            lineHeight: "1.6",
            marginTop: "-4px",
          }}
        >
          {t(
            "titleTool.resultsIntro",
            "These are ready-to-test title options. Pick the strongest one based on click appeal, clarity, and fit for your audience."
          )}
        </div>
      )}

      <div style={{ display: "grid", gap: "14px" }}>
        {loading ? (
          <ToolResultSkeleton isDark={isDark} count={3} />
        ) : (
        titles.map((title, index) => {
          const safeScore = Number(title.score) || 0;
          const tone = getScoreTone(safeScore);

          return (
            <ResultCard
              key={`${title.text}-${index}`}
              colors={colors}
              index={index}
            >
              <div style={{ color: colors.text, fontWeight: 700, fontSize: "18px", lineHeight: "1.5" }}>
                {title.text}
              </div>

              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "10px" }}>
                {title.archetype && (
                  <span
                    style={{
                      background: colors.chipBg,
                      borderRadius: "999px",
                      padding: "6px 10px",
                      fontSize: "12px",
                      color: colors.chipText,
                      fontWeight: 600,
                    }}
                  >
                    {formatArchetypeLabel(title.archetype)}
                  </span>
                )}

                <span
                  style={{
                    background: tone.pillBg,
                    borderRadius: "999px",
                    padding: "6px 10px",
                    fontSize: "12px",
                    color: tone.pillText,
                    fontWeight: 700,
                  }}
                >
                  {t("titleTool.labels.score", "Score")}: {safeScore}
                </span>

                {title.keyword_focus && (
                  <span
                    style={{
                      background: colors.chipBg,
                      borderRadius: "999px",
                      padding: "6px 10px",
                      fontSize: "12px",
                      color: colors.chipText,
                      fontWeight: 600,
                    }}
                  >
                    {title.keyword_focus}
                  </span>
                )}
              </div>

              {renderScoreBar(safeScore)}

              <ActionRow style={{ marginTop: "14px" }}>
                <CopyButton
                  copied={copiedId === index}
                  onClick={() => copyTitle(title.text, index)}
                >
                  {copiedId === index
                    ? t("titleTool.actions.copied", "Copied")
                    : t("titleTool.actions.copy", "Copy")}
                </CopyButton>

                <SaveStudioAssetButton
                  toolType="titles"
                  title={`Title Option • ${sharedTopic || t("titleTool.fallbackUntitledTopic", "Untitled Topic")}`}
                  topic={sharedTopic}
                  platform="YouTube"
                  content={title.text || ""}
                  meta={{
                    score: title.score,
                    label: title.label,
                    archetype: title.archetype || "",
                    keyword_focus: title.keyword_focus || "",
                  }}
                />
              </ActionRow>
            </ResultCard>
          );
        })
        )}
      </div>
    </div>
    </MotionSurface>
  );
};

export default TitleTool;